import { INote } from './note.type'
import { IProcedureDentalCondition } from './treatmentPlan.types'

import { Clinic } from './location.type'
import { ProviderColor } from './provider.types'

export type Appointment = {
  Id: number
  patient_name: string
  patient_id: string
  phone_number: string
  appointment_status: AppointmentStatus
  appointment_type_id: number
  appointment_type: string
  is_confirmed: boolean
  note: string
  appointment_start_date_time: string | Date
  appointment_end_date_time: string | Date
  provider_name: string
  provider_id: string
  provider_primary_color: string
  provider_secondary_color: string
  provider_third_color: string
  provider_fourth_color: string
  inform_patient_via_phone: boolean
  inform_patient_via_sms: boolean
  inform_patient_via_email: boolean
  inform_patient_via_app: boolean
  reason_for_visit: string
  is_recurring: boolean
  visit_every?: string
  end_after?: string
  recurrence_interval?: number
  end_count?: number
  end_until?: string
  clinic_name: string
  insurance_id: string
  policy_number: string
  confirmed: number // removed this field
  is_hygiene: boolean
  has_insurance: boolean
  has_allergies: boolean
  has_high_blood_pressure: boolean
  has_profile_tag: boolean
  template: string
  files?: { name: string; size: string }[]
  created_by: string
  updated_by: string
  pref_dentist?: string
  pref_hygienist?: string
  insurance_status: InsuranceStatus
  patient_status: PatientStatus
  form_status: FormStatus
  payment_status: PaymentStatus
  other_status?: OtherStatus
  isReadOnly: boolean
  duration: string
  procedure_room_id: string // operatory_id
  recurrenceRule?: string
}

export type TimeDuration = {
  id: string
  start: string
  end: string
  duration: string
}

export enum AvailabilityStatus {
  AVAILABLE = 'AVAILABLE',
  NO_WORKING_SCHEDULE = 'NO_WORKING_SCHEDULE',
  PROVIDER_UNAVAILABLE = 'PROVIDER_UNAVAILABLE',
  PROVIDER_ALREADY_BOOKED = 'PROVIDER_ALREADY_BOOKED',
  PATIENT_ALREADY_BOOKED = 'PATIENT_ALREADY_BOOKED',
  ROOM_ALREADY_BOOKED = 'ROOM_ALREADY_BOOKED',
}

export enum AppointmentStatus {
  Confirmed = 0,
  Completed = 1,
  Pending = 2,
  Alert = 3,
  FollowUp = 4,
  NewPatient = 5,
  Cancelled = 6,
  NoShow = 7,
  Late = 8,
  Unconfirmed = 9,
  CheckIn = 10,
  InTreatment = 11,
  CheckOut = 12,
}

export enum InsuranceStatus {
  InsuredVerify = 0,
  NotInsured = 1,
  InsuredNotVerified = 2,
  InsuranceIssue = 3,
}

export enum PatientStatus {
  MedicalCondition = 0,
  CriticalAttentionNeeded = 1,
  HasAllergy = 2,
}

export enum FormStatus {
  FormCompleted = 0,
  FormIncomplete = 1,
}

export enum PaymentStatus {
  PaymentCompleted = 0,
  MissedPayments = 1,
}

export type PreAuthStatus =
  | 'pending'
  | 'approved'
  | 'verified'
  | 'denied'
  | 'na'

export enum OtherStatus {
  MedicalTest = 0,
}

export interface ProviderDetails {
  provider_id: string
  user_id: string
  clinic_id: string
  operatory_id: string
  first_name: string
  middle_name?: string
  last_name: string
  username: string
  gender: string
  date_of_birth: string // Use `Date` if you will parse it to a date object
  pronouns: string
  marital_status: string
  address: string
  city: string
  state: string
  zip: string
  cellphone: string
  email: string
  home_number?: string
  emergency_contact?: string
  state_license_number: string
  state_where_licensed_issued: string
  state_license_expiry: string // Use `Date` if you will parse it to a date object
  state_rx_id: string
  dea_number: string
  tax_id_type: string
  tax_id_number: string
  npi_type: string
  npi_number: string
  work_position: string
  specialty: string
  provider_color: ProviderColor
  clinic?: Clinic
  working_days: string[] // Array of day abbreviations like ["M", "T", "W"]
  working_hours: string // Use a more structured type if needed (e.g., start and end times)
  created_by: string
  updated_by: string
  is_hidden: boolean
  date_time_created_at: string // Use `Date` if you will parse it to a date object
  date_time_updated_at: string // Use `Date` if you will parse it to a date object
}

export interface IOperatory {
  operatory_id: string // Primary key, UUID
  clinic_id: string // Foreign key, UUID
  name: string // Name of the operatory
  color: string // Color assigned to the operatory
  is_hidden: boolean // Visibility flag
  created_by: string // Created by user, foreign key, UUID
  updated_by: string // Updated by user, foreign key, UUID
  providers: IProvider[] // Array of providers associated with the operatory
  operatory_providers?: { provider_id: string }[]
  clinic: IClinic // Clinic associated with the operatory
}

export interface IAppointment {
  Id: number
  appointment_id: string
  patient_id: string
  clinic_id: string | null
  operatory_id: string
  insurance_id: string | null
  provider_id: string
  provider_hygienist: string | null
  appointment_type_id: string
  appt_status: number
  appt_date: string
  appt_start: string
  appt_end: string
  duration: number
  is_recurring: boolean
  recurring_visit_id: string
  is_insured: boolean
  tp_id: string | null
  lab_order_id: string[] | null
  files?: { name: string; size: string }[]
  template_id: string | null
  contact_via: string[]
  payment_status: number
  is_read_only: boolean
  created_by: string
  updated_by: string
  is_hidden: boolean
  date_time_created_at: string // ISO date string
  date_time_updated_at: string // ISO date string
  patient: IPatient
  clinic: IClinic
  operatory: IOperatory
  provider: IProvider
  appointmenttype: IAppointmentType
  insurance: IInsurance
  template: ITemplate
  hygienist: IProvider
  procedure_logs: IProcedureLogs[]
  lab_order: ILabOrder[]
  appointment_start_date: string
  appointment_end_date: string
  notes: INote[] | any[]
}

export interface ILabOrder {
  lab_case_id: string
  patient_id: string
  laboratory_id: string
  appointment_id: string
}
export interface IProcedureLogs {
  proc_log_id: string
  patient_id: string
  appointment_id: string | null
  provider_id: string
  proc_date: Date | string
  area_type: number | null
  area: string | null
  tooth: string | null
  surf: string | null
  proc_code: string
  priority: number
  proc_status: number
  proc_id: string
  proc_description: string
  insurance_plan_id: string | null
  clinic_fee: number
  allowed_amount: number
  write_off: number
  estimated_insurance_pay: number
  estimated_patient_resp: number
  source: number
  source_id: string
  tooth_details?: any[]
  id?: string
  parentId?: string | null
}

export interface IToothDetails {
  area_type: number
  area?: string
  tooth_num?: number
  surf?: string[]
}
export interface IAppointmentType {
  appointment_type_id: string
  date_time_created_at: Date | string
  date_time_updated_at: Date | string
  name: string
  appointmenttypesubcategories: any[]
}
interface IFile {
  name: string
  size: string
}

interface IPatient {
  patient_id: string
  first_name: string
  last_name: string
  cellphone: string
}

interface IInsurance {
  insurance_id: string
  insurance_name: string
}

export interface ITemplate {
  template_id: string
  template_name: string
  description: string
  is_hidden: boolean
  created_by: string
  updated_by: string
}

export interface IProvider {
  provider_id: string
  first_name: string
  last_name: string
  username?: string
  provider_color: ProviderColor
  operatory_id?: string
  work_position?: string
}

interface IProcedure {
  procedure_id: string
  procedure_code: string
  description: string
  layman_term: string
}

interface IClinic {
  clinic_id: string
  clinic_name: string
  description: string
}

export interface Procedure {
  procedure_id: string
  procedure_code: string
  description: string
  procedure_settings: any | null
  procedure_time: string
  treatment_area: number
  no_bill_insurance: boolean
  is_prosth: boolean
  default_note: string
  is_hygiene: number
  is_taxed: number
  paint_type: number
  layman_term: string
  base_units: number | null
  created_by: string
  updated_by: string
  is_hidden: boolean
  date_time_created_at: string
  date_time_updated_at: string
  procedure_dental_conditions?: IProcedureDentalCondition[]
}

export interface InsurancePatient {
  insurance_patient_id: string
  insurance_plan_id: string
  insurance_sub_id: string
  patient_id: string
  relation_to_subscriber: string
  date_effective_from: string
  date_effective_to: string
  verified_on: string
  InsurancePlan: IInsurancePlan
}

export interface PatientWithInsurance {
  patient_id: string
  user_id: string
  preferred_provider: string
  preferred_hygienist: string
  clinic_id: string
  title: string
  first_name: string
  middle_name: string
  last_name: string
  preferred_name: string | null
  date_of_birth: string | null
  gender: string | null
  pronouns: string | null
  marital_status: string
  address: string
  city: string
  state: string
  zip: string
  profession: string
  company_name: string
  cellphone: string
  email: string
  home_phone: string
  work_phone: string
  allergies?: string | null
  medical_conditions?: string | null
  allergy_ids?: string[]
  medical_condition_ids?: string[]
  patient_allergies?: import('./allergy-medical-condition.types').PatientAllergy[]
  patient_medical_conditions?: import('./allergy-medical-condition.types').PatientMedicalCondition[]
  has_insurance: boolean
  policy_number: string | null
  insurance_expiry_date: string | null
  join_referral_program: boolean
  preferred_appointment_time: string
  contact_via_phone: boolean
  contact_via_sms: boolean
  contact_via_email: boolean
  contact_via_app: boolean
  balance_due: string
  new_patient: boolean
  patient_status: number
  photo: string | null
  photo_file?: any
  referral_source: number
  is_hidden: boolean
  created_by: string
  updated_by: string
  date_time_created_at: string
  date_time_updated_at: string
  fee_schedule_id: string | null
  household_id: string | null
  insurance_plan_id: string | null
  pat_ins: InsurancePatient[]
  insurance_plans: IInsurancePlan[]
}

export interface IInsurancePlan {
  insurance_plan_id: string | null
  plan_name: string
  plan_id: string
  plan_note: string
  plan_type: number
  group_no: string
  group_name: string
  status: number
  is_primary: boolean
  insurance_sub_id: string
  fees: any[] | null
  benefits: any[] | null
  services: any[] | null
  use_alt_code: boolean
  claims_use_ucr: boolean
  employer_id: string | null // fk
  carrier_id: string // fk
  carrier: any
  is_medical: boolean
  show_base_units: boolean
  code_substitute_none: boolean
  is_hidden: boolean
  month_renew: number
  rx_bin: string
  cob_rule: number
  created_by: string // fk
  hide_from_verify_list: boolean
  ortho_type: number
  ortho_auto_procedure_frequency: number
  ortho_auto_proc_code_num_override: string
  ortho_auto_fee_billed: number
  ortho_auto_claim_days_wait: number
  has_ppo_substitute_writeoffs: boolean
  exclusion_fee_rule: number
  is_blue_book_enabled: boolean
  zero_write_offs_on_annual_max_override: number
  zero_write_offs_on_frequency_or_aging_override: number
  per_visit_patient_amount: number
  per_visit_insurance_amount: number
  division: string
  fee_guide: string
  claim_filing_type: string
  fee_guide_to_send_on_claims: string
  benefits_advisor_plan: string
  date_time_created_at: Date
  date_time_updated_at: Date
}

export interface ICommonProcedure {
  common_proc_id: string
  procedure_id: string
  position?: number
  is_hidden: boolean
  created_by: string
  updated_by: string | null
  procedure?: Procedure
}

// NEW DATA TYPES

export interface AppointmentRequest {
  appointment_id?: string
  patient_id: string
  clinic_id?: string | null
  operatory_id: string
  insurance_plan_id?: string | null
  provider_id: string
  provider_hygienist?: string | null
  appointment_type_id: string
  appointment_type_subcategory_id: string
  appt_status: number
  appt_date: string // 'YYYY-MM-DD'
  appt_start: string // 'HH:mm'
  appt_end: string // 'HH:mm'
  duration: number
  is_recurring: boolean
  recurring_visit_id?: string | null
  recurring_every: number | null
  recurring_for: number | null
  recurring_days: string | null
  recurring_start_date: string | null
  recurring_end_date: string | null
  is_insured: boolean
  tp_id?: string | null
  files?: { name: string; size: string }[]
  form_template_ids?: string[]
  template_id?: string | null
  contact_via: string[]
  payment_status: number
  is_read_only?: boolean
  created_by: string
  updated_by: string
  procedure_logs?: any[]
  lab_order?: any[]
  notes?: Partial<INote>[]
}

export interface AppointmentResponse {
  appointment_id: string
  patient_id: string
  clinic_id: string | null
  operatory_id: string
  insurance_id: string | null
  provider_id: string
  provider_hygienist: string | null
  appointment_type_id: string
  appointment_type_subcategory_id: string
  appt_status: number
  appt_date: string
  appt_start: string
  appt_end: string
  duration: number
  is_recurring: boolean
  recurring_visit_id: string | null
  recurring_every: number | null
  recurring_for: number | null
  recurring_days: string | null
  recurring_start_date: string | null
  recurring_end_date: string | null
  is_insured: boolean
  tp_id: string | null
  files: { name: string; size: string }[]
  form_template_ids?: string[]
  /** Included when appointments are loaded with form assignment rows from the API */
  formAssignments?: { form_template_id: string }[]
  template_id: string | null
  contact_via: string[]
  payment_status: number
  is_read_only: boolean
  created_by: string
  updated_by: string
  /** True when the AI receptionist created or last updated this appointment. */
  ai_assisted?: boolean
  is_hidden: boolean
  date_time_created_at: string
  date_time_updated_at: string
  insurance_plan_id?: string | null
  procedure_id?: string | null

  patient: {
    patient_id: string
    first_name: string
    last_name: string
    cellphone: string
    preferred_provider_info?: {
      first_name?: string
      last_name?: string
    }
    preferred_hygienist_info?: {
      first_name?: string
      last_name?: string
    }
    preferredProvider?: {
      first_name?: string
      last_name?: string
    }
    preferredHygienist?: {
      first_name?: string
      last_name?: string
    }
  }

  insurance?: {
    insurance_patient_id: string
    insurance_plan_id: string
    insurance_sub_id: string
  }

  template?: {
    template_id: string
    template_name: string
    description: string
  } | null

  provider: {
    provider_id: string
    first_name: string
    last_name: string
    provider_color: ProviderColor
  }

  appointmenttype: {
    appointment_type_id: string
    name: string
  }

  operatory?: {
    operatory_id: string
    name: string
  }

  pre_auth_status?: PreAuthStatus

  procedure_logs: any[]
  lab_orders: ILabCases[]

  notes: INote[]

  Id: number
  appointment_start_date: string
  appointment_end_date: string
}

export interface ILabCase {
  lab_case_id: string
  patient_id: string
  laboratory_id: string
  provider_id: string
  date_time_due: Date
  date_time_created: Date
  date_time_sent: Date
  date_time_received: Date
  date_time_checked: Date
  instructions: string
  lab_fee: number
  invoice_num: string
  is_hidden: boolean
  date_time_created_at: Date
  date_time_updated_at: Date
}

export interface ILabCases {
  lab_case_id: string
  patient_id: string
  laboratory_id: string
  provider_id: string
  date_time_due: string // ISO string (Date)
  date_time_created: string
  date_time_sent: string
  date_time_received: string
  date_time_checked: string
  instructions: string
  lab_fee: string // kept as string since the sample data had it as "75.00"
  invoice_num: string
  is_hidden: boolean
  date_time_created_at: string
  date_time_updated_at: string
}
